import { isTemplateComponent } from '@unifyapps/carbon/no-code/components/BlockPicker/templateFilters';
import type { CustomBlockEntity } from '@unifyapps/defs/blocks/Custom/types';
import { getMethodSchema } from '@unifyapps/defs/blocks/common/methodSchema/scrollIntoView';
import { stripEmptyOneOfFields } from '@unifyapps/defs/blocks/common/stripEmptyOneOfFields';
import type { VariantSchema } from '@unifyapps/defs/blocks/common/themeVariantsSchema';
import type { ThemeVariantDimension } from '@unifyapps/defs/theme/types';
import type {
  BlockId,
  BlockStateUnionType,
  BlockType,
  BlockTypeUnionType,
} from '@unifyapps/defs/types/block';
import type { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import type { EventTypeEnum } from '@unifyapps/defs/types/event';
import type { FileVisibility } from '@unifyapps/defs/types/fileVisibility';
import { ApplicationType } from '@unifyapps/defs/types/interface';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import { isUndefined } from 'lodash';
import keyBy from 'lodash/keyBy';
import type { JSONTreeSchemaObject } from '../../../types';
import type { GlobalStateStoreType } from '../../stores/GlobalStateStore';
import type { PropertyPanelHistoryItem } from '../../stores/InterfaceStore/builder';
import type { InsertBlockContext } from '../../stores/InterfaceStore/builder/actions/insertBlock';
import {
  type BlockEditorDefinition,
  type BlockStateDefinition,
  type ComponentType,
  type ContentSchemaParams,
  isBlockAllowedForInterface,
} from '../../types/blockDefinition';
import {
  BLOCKS_TO_HIDE,
  BLOCKS_TO_HIDE_MOBILE,
  BLOCKS_VISIBLE_ONLY_IN_TEMPLATE_COMPONENT,
  COMMON_BLOCK_METHODS,
  COMMON_BLOCK_METHODS_MOBILE,
} from './constants';
import { defaultAppearanceToBlockData, defaultContentToBlockData } from './defaultToBlockData';
import { defaultAppearanceToFormData, defaultContentToFormData } from './defaultToFormData';
import type { BlockRendererComponentType, BlocksRecordType, CustomBlockDefinitions } from './types';

const isErrorLogged: Record<string, boolean> = {};

class BlocksRegistry {
  private readonly name: string;
  private readonly blockRenderer: BlockRendererComponentType;
  private blockEditorDefinitions?: BlockEditorDefinition[];
  private blockStateDefinitionsByType?: Record<string, BlockStateDefinition | undefined>;
  private blockEditorDefinitionsByType?: Record<string, BlockEditorDefinition | undefined>;
  private readonly blocks: Partial<BlocksRecordType>;
  private preloadedComponent: Record<string, ComponentType | undefined> = {};
  private isComponentPreloaded: Record<string, boolean | undefined> = {};
  private customBlockDefinitions?: CustomBlockDefinitions;

  constructor({
    blockRenderer,
    blocks,
    name,
    blockEditorDefinitions,
    blockStateDefinitions,
    customBlockDefinitions,
  }: {
    name: string;
    blockRenderer: BlockRendererComponentType;
    blocks: Partial<BlocksRecordType>;
    blockEditorDefinitions?: BlockEditorDefinition[];
    blockStateDefinitions?: BlockStateDefinition[];
    customBlockDefinitions?: CustomBlockDefinitions;
  }) {
    this.name = name;
    this.blockRenderer = blockRenderer;
    this.blocks = blocks;
    this.blockEditorDefinitions = blockEditorDefinitions;
    this.blockStateDefinitionsByType = keyBy(blockStateDefinitions, 'type');
    this.blockEditorDefinitionsByType = keyBy(blockEditorDefinitions, 'type');
    this.customBlockDefinitions = customBlockDefinitions;
  }

  getBlockStateDefinition(name: BlockTypeUnionType) {
    const blockStateDefinition = this.blockStateDefinitionsByType?.[name];
    if (blockStateDefinition) return blockStateDefinition;
    if (!this.blocks[name]) {
      const errorMessage = `Block state definition not found for block type: ${name}`;
      if (!isErrorLogged[errorMessage]) {
        console.error(errorMessage);
        isErrorLogged[errorMessage] = true;
      }
    }
  }

  getBlockBehavior(name: BlockTypeUnionType) {
    return this.getBlockStateDefinition(name)?.getBlockBehavior?.();
  }

  getBlockStateDefinitionsTypes() {
    return this.blockStateDefinitionsByType ? Object.keys(this.blockStateDefinitionsByType) : [];
  }

  getBlockEditorDefinition(name: BlockTypeUnionType) {
    const blockEditorDefinition = this.blockEditorDefinitionsByType?.[name];
    if (blockEditorDefinition) return blockEditorDefinition;
    if (!this.blocks[name]) {
      const errorMessage = `Block editor definition not found for block type: ${name}`;
      if (!isErrorLogged[errorMessage]) {
        console.error(errorMessage);
        isErrorLogged[errorMessage] = true;
      }
    }
  }

  hasBlockEditorDefinitions() {
    return Boolean(this.blockEditorDefinitions?.length);
  }

  getBlockVersion(name: BlockTypeUnionType) {
    return this.getBlockStateDefinition(name)?.getBlockVersion() ?? 0;
  }

  getBlockVersionByBlock(block: BlockType) {
    return block.v ?? 0;
  }

  getBlockComponent(name: BlockTypeUnionType) {
    const deprecatedPatternBlock = this.blocks[name] as ComponentType | undefined;
    if (deprecatedPatternBlock) return deprecatedPatternBlock;
    const block = this.preloadedComponent[name];
    if (block) return block;

    return this.getBlockStateDefinition(name)?.getComponent();
  }

  getBlockInitialFormData(
    name: BlockTypeUnionType,
    device?: DeviceVariantType,
    context?: InsertBlockContext,
  ) {
    return this.getBlockEditorDefinition(name)?.getInitialFormData(device, context);
  }

  getBlockInfo(name: BlockTypeUnionType) {
    return this.getBlockEditorDefinition(name)?.info;
  }

  getBlockEmbeddedEntityReferences(name: BlockTypeUnionType) {
    return this.getBlockEditorDefinition(name)?.getEmbeddedEntityReferences;
  }

  /**
   * resolves the theme-manager component id for a block. blocks may override
   * via `getThemeComponentId`; fallback is lowercased componentType.
   */
  getBlockThemeComponentId(block: BlockType): string {
    const name = block.component.componentType;
    const def = this.getBlockEditorDefinition(name);
    return def?.getThemeComponentId?.(block) ?? name.toLowerCase();
  }

  getBlockContentSchema(
    block: BlockType,
    params: {
      interfaceId: string;
      applicationType?: ApplicationType;
      device?: DeviceVariantType;
      isAppConfigHidden?: boolean;
      themeVariantsConfig?: ThemeVariantDimension[];
      modules?: InterfacePageEntity[];
      /** app-level default from Advanced Settings → File Management */
      defaultFileVisibility?: FileVisibility;
    },
  ) {
    const name = block.component.componentType;
    const result = this.getBlockEditorDefinition(name)?.getContentSchema(block, params) ?? {};
    return stripEmptyOneOfFields(result);
  }

  getBlockAppearanceSchema(
    block: BlockType,
    params?: {
      getGlobalStateStoreState: () => GlobalStateStoreType;
      applicationType?: ApplicationType;
      device?: DeviceVariantType;
      themeVariantsConfig?: ThemeVariantDimension[];
      /** typography's variant+weight overrides — blocks opt in by reading this */
      typographyVariantOverrides?: Record<string, VariantSchema>;
    },
  ) {
    const name = block.component.componentType;
    const result = this.getBlockEditorDefinition(name)?.getAppearanceSchema(block, params) ?? {};
    return stripEmptyOneOfFields(result);
  }

  getSlotOrder(block: BlockType) {
    return this.getBlockStateDefinition(block.component.componentType)?.getSlotOrder?.(block);
  }

  getBlockCompositionData(name: BlockTypeUnionType, device?: DeviceVariantType) {
    return this.getBlockEditorDefinition(name)?.getCompositionData?.(device);
  }

  getPanelResolver(name: BlockTypeUnionType) {
    return this.getBlockEditorDefinition(name)?.panelResolver;
  }

  getBlockEditorComponent(block: BlockType | undefined) {
    if (isUndefined(block)) {
      return null;
    }
    const name = block.component.componentType;

    return this.getBlockEditorDefinition(name)?.getEditorComponent?.();
  }

  getUseBlockRef(name?: BlockTypeUnionType) {
    return name ? this.getBlockStateDefinition(name)?.useBlockRef : false;
  }

  getBlockStateSchema(
    name: BlockTypeUnionType,
    block: BlockType,
    {
      getDataSourceOutputSchema,
      modules,
      activeBlockId,
      isBlockAncestorOfActiveBlockFn,
      isBlockAncestorOfActiveBlock,
      getGlobalStateStoreState,
      selectedEventId,
      propertyPanelActiveState,
      // parent-Repeatable schema accumulator from useFetchAndSetBlockStateSchema's
      // chain-aware scheduler. block defs whose `data` may reference another block's
      // `context.*` (today: Repeatable) consume it; most defs ignore it. see
      schemas,
    }: {
      getDataSourceOutputSchema: (params: {
        block: BlockType;
        data: string;
        schemas?: JSONTreeSchemaObject[];
      }) => object | undefined;
      getGlobalStateStoreState: () => GlobalStateStoreType;
      modules: InterfacePageEntity[] | undefined;
      activeBlockId: BlockId;
      isBlockAncestorOfActiveBlockFn?: (blockId: BlockId) => boolean;
      isBlockAncestorOfActiveBlock?: boolean;
      selectedEventId?: EventTypeEnum;
      propertyPanelActiveState?: PropertyPanelHistoryItem;
      schemas?: JSONTreeSchemaObject[];
    },
  ) {
    return this.getBlockEditorDefinition(name)?.stateSchema?.(block, {
      getDataSourceOutputSchema,
      modules,
      activeBlockId,
      isBlockAncestorOfActiveBlockFn,
      getGlobalStateStoreState,
      isBlockAncestorOfActiveBlock,
      selectedEventId,
      propertyPanelActiveState,
      schemas,
    });
  }

  getBlockControlMethods(
    name: BlockTypeUnionType,
    block: BlockType,
    applicationType: ApplicationType | undefined,
    options?: {
      modules?: InterfacePageEntity[];
    },
  ) {
    const methods =
      this.getBlockStateDefinition(name)?.getBlockControlMethods?.(
        block,
        applicationType,
        options,
      ) ?? [];

    return [
      ...methods,
      ...(applicationType === ApplicationType.MOBILE
        ? COMMON_BLOCK_METHODS_MOBILE
        : COMMON_BLOCK_METHODS),
    ];
  }

  getBlockRenderer() {
    return this.blockRenderer;
  }

  getBlockInitialState(
    name: BlockTypeUnionType,
    block: BlockType,
  ): BlockStateUnionType | undefined {
    const visibility = block.visibility;
    const additional = block.additional;
    const initialState = this.getBlockStateDefinition(name)?.initialStateGetter(block);
    return {
      ...initialState,
      visibility,
      additional,
    } as BlockStateUnionType | undefined;
  }

  getShouldBlockSkipErrorBoundary(name?: BlockTypeUnionType) {
    // skip if blockPreload is not defined
    return name
      ? this.getBlockStateDefinition(name)?.preload?.()?.blockPreload === undefined
      : false;
  }

  getBlockMethodSchema(
    block: BlockType,
    methodName: string,
    options?: { modules?: InterfacePageEntity[] },
  ) {
    const blockComponent = block.component;
    const blockComponentType = blockComponent.componentType;

    // first check block-specific method schema
    if (methodName === 'scrollIntoView') {
      return getMethodSchema({ methodName });
    }

    // fallback to common block method schemas
    const blockMethodSchema = this.getBlockEditorDefinition(
      blockComponentType,
    )?.controlMethodSchemaGetter?.()(methodName, block, options);

    if (blockMethodSchema) {
      return blockMethodSchema;
    }

    return undefined;
  }

  preloadBlock(name: BlockTypeUnionType, block?: BlockType) {
    const { blockPreload, miscellaneous } = this.getBlockStateDefinition(name)?.preload?.() ?? {};

    if (!this.isComponentPreloaded[name]) {
      void blockPreload?.().then((_block) => {
        this.setPreloadedComponent(name, _block.default);
      });
      this.isComponentPreloaded[name] = true;
    }

    miscellaneous?.(block).forEach((misc) => {
      void misc();
    });
  }

  shouldPreloadBlock(name: BlockTypeUnionType) {
    return this.getBlockStateDefinition(name)?.preload?.()?.runner ?? false;
  }

  getEventTargetIds(name: BlockTypeUnionType, block: BlockType) {
    return this.getBlockStateDefinition(name)?.getEventTargetIds?.(block);
  }

  getRegistryName() {
    return this.name;
  }

  getAllDefinitions() {
    return this.blockEditorDefinitions;
  }

  getBlockPickerOptions({
    applicationType,
    ancestorBlockTypes,
    interfaceId,
  }: {
    applicationType?: ApplicationType;
    ancestorBlockTypes?: Set<string>;
    interfaceId: string | undefined;
  }) {
    const blocksToHide =
      applicationType === ApplicationType.MOBILE
        ? [...BLOCKS_TO_HIDE, ...BLOCKS_TO_HIDE_MOBILE]
        : [...BLOCKS_TO_HIDE];

    if (!isTemplateComponent) {
      blocksToHide.push(...BLOCKS_VISIBLE_ONLY_IN_TEMPLATE_COMPONENT);
    }

    return this.blockEditorDefinitions?.filter((blockDefinition) => {
      if (blocksToHide.includes(blockDefinition.type)) {
        return false;
      }
      const { visibleIfAncestorHasType, hiddenIfAncestorHasType, allowedInterfaceIds } =
        blockDefinition.info;

      if (visibleIfAncestorHasType) {
        if (!ancestorBlockTypes?.size) return false;
        if (!visibleIfAncestorHasType.some((type) => ancestorBlockTypes.has(type))) return false;
      }

      if (hiddenIfAncestorHasType && ancestorBlockTypes?.size) {
        if (hiddenIfAncestorHasType.some((type) => ancestorBlockTypes.has(type))) return false;
      }

      if (!isBlockAllowedForInterface(allowedInterfaceIds, interfaceId)) return false;

      return true;
    });
  }

  isBlockAllowedForInterface(name: BlockTypeUnionType, interfaceId: string | undefined) {
    return isBlockAllowedForInterface(this.getBlockInfo(name)?.allowedInterfaceIds, interfaceId);
  }

  getCustomBlockManifestUrlsForType(type: BlockTypeUnionType): { main: string; style?: string }[] {
    const editorDefinition = this.blockEditorDefinitionsByType?.[type];
    if (editorDefinition?.info.category !== 'Custom') return [];

    const stateDefinition = this.blockStateDefinitionsByType?.[type];
    const manifests = stateDefinition?.getAllManifests?.() ?? [];

    const urls: { main: string; style?: string }[] = [];
    const seen = new Set<string>();
    for (const manifest of manifests) {
      if (!manifest.main || seen.has(manifest.main)) continue;
      seen.add(manifest.main);
      urls.push({ main: manifest.main, style: manifest.style });
    }
    return urls;
  }

  getAllBlocksIcons() {
    return Object.entries(this.blockEditorDefinitions ?? {}).reduce<Record<string, string>>(
      (acc, [, value]) => {
        acc[value.type] = value.info.icon;
        return acc;
      },
      {},
    );
  }

  getBlockImplicitDependsOn({
    name,
    block,
    interfacePageId,
  }: {
    name: BlockTypeUnionType;
    block: BlockType;
    interfacePageId: string;
  }) {
    return this.getBlockStateDefinition(name)?.getImplicitDependencies?.(block, interfacePageId);
  }

  getBlockImplicitDependencyGroups(name: BlockTypeUnionType) {
    return this.getBlockStateDefinition(name)?.getImplicitDependencyGroups?.();
  }

  getBlockIcon(name: BlockTypeUnionType) {
    return this.getBlockInfo(name)?.icon;
  }

  getBlockCategory(name: BlockTypeUnionType) {
    return this.getBlockInfo(name)?.category;
  }

  getBlockContentSchemaTransformer(name: BlockTypeUnionType) {
    const transformers = this.getBlockEditorDefinition(name)?.getContentSchemaTransformer?.();

    return {
      toFormData: transformers?.toFormData ?? defaultContentToFormData,
      toBlockData: transformers?.toBlockData ?? defaultContentToBlockData,
    };
  }

  getBlockAppearanceSchemaTransformer(name: BlockTypeUnionType) {
    const transformers =
      this.getBlockEditorDefinition(name)?.getBlockAppearanceSchemaTransformer?.();

    return {
      toFormData: transformers?.toFormData ?? defaultAppearanceToFormData,
      toBlockData: transformers?.toBlockData ?? defaultAppearanceToBlockData,
    };
  }

  getKeysToOmitInComputation(name: BlockTypeUnionType) {
    return this.getBlockStateDefinition(name)?.getKeysToOmitInComputation?.() ?? [];
  }

  setPreloadedComponent(name: string, component: ComponentType) {
    this.preloadedComponent[name] = component;
  }

  addCustomBlock(customBlock: CustomBlockEntity) {
    if (!this.customBlockDefinitions) return;

    const { StateDefinition, EditorDefinition } = this.customBlockDefinitions;
    const stateDefinition = new StateDefinition(customBlock);
    const type = stateDefinition.type;

    // add state definition if not present
    if (
      this.blockStateDefinitionsByType &&
      !Object.prototype.hasOwnProperty.call(this.blockStateDefinitionsByType, type)
    ) {
      this.blockStateDefinitionsByType[type] = stateDefinition;
    }

    // add editor definition if not present and all required structures exist
    if (
      EditorDefinition &&
      this.blockEditorDefinitions &&
      this.blockEditorDefinitionsByType &&
      !Object.prototype.hasOwnProperty.call(this.blockEditorDefinitionsByType, type)
    ) {
      // avoid duplicate by type in array
      if (!this.blockEditorDefinitions.some((def) => def.type === type)) {
        const editorDefinition = new EditorDefinition(customBlock);
        this.blockEditorDefinitions.push(editorDefinition);
        this.blockEditorDefinitionsByType[type] = editorDefinition;
      }
    }
  }
}

export default BlocksRegistry;
